// Privacy Policy + Terms of Use text, in English and Arabic. "{name}" is replaced with the current
// teacher's name (tenant.name) at render time, since every teacher domain serves this same page.
// Kept in line with what the platform actually collects/does -- if a feature starts collecting new
// data (or stops), update the matching section here too. Also linked from the desktop app's
// footers and used as the Microsoft Store privacy-policy URL (/privacy).
export const LAST_UPDATED = "2026-10-03";

const en = {
  privacy: {
    title: "Privacy Policy",
    intro:
      "This policy explains what information the {name} learning platform (the website and the desktop app) collects, why we collect it, and how we protect it. By creating an account or using the platform you agree to this policy.",
    sections: [
      {
        heading: "1. Information we collect",
        items: [
          "Account information: full name, email, student mobile number, parent/guardian mobile number, governorate and city, school grade, gender and an optional profile photo. Your password is stored only in hashed (encrypted) form.",
          "Learning information: courses and subscriptions, lessons you open and how far you watched, exam answers and results, messages you send through the inbox, and booked sessions.",
          "Device and security information: a device identifier and device name (used to enforce the number of devices allowed per account), and security events such as screenshot or screen-recording attempts detected while the app is open (the shortcut used or the name of the recording program, with the time).",
          "Technical information: IP address, browser or app version and server logs needed to run and secure the service.",
        ],
      },
      {
        heading: "2. How we use your information",
        items: [
          "To create your account, give you access to the content you subscribed to, and save your progress.",
          "To enforce subscription periods and the allowed number of devices.",
          "To protect the content: videos show a moving watermark with your name and student number, and detected recording attempts may lead to warnings or account suspension (see the Terms of Use).",
          "To contact you or your parent/guardian about your account, sessions and results.",
          "To keep the platform secure, fix problems and improve the service.",
        ],
      },
      {
        heading: "3. Sharing your information",
        items: [
          "We do not sell your personal information.",
          "Your teacher ({name}) and their authorized assistants can see your profile, subscriptions, progress, exam results and messages.",
          "We use trusted service providers to host the platform and store files (servers and cloud storage). They process data only to provide that service.",
          "Some lessons may play videos embedded from YouTube or Vimeo; those services apply their own privacy policies.",
          "We may disclose information if required by law or to protect the rights and safety of the platform and its users.",
        ],
      },
      {
        heading: "4. Data stored on your device",
        items: [
          "The website and desktop app store your login session, language choice and some cached data (such as your schedule) on your device so you stay signed in and the app loads faster. Signing out removes the login session.",
        ],
      },
      {
        heading: "5. Security",
        items: [
          "All connections to the platform are encrypted (HTTPS), passwords are hashed, and access to data is restricted by role. No system is 100% secure, but we take reasonable measures to protect your information.",
        ],
      },
      {
        heading: "6. Data retention",
        items: [
          "We keep your information while your account is active. If your account is deleted we remove or anonymize your personal information, except what we must keep for legal, security or dispute purposes.",
        ],
      },
      {
        heading: "7. Your rights",
        items: [
          "You can ask to access, correct or delete your personal information at any time through the Contact page. Some changes (such as your name or phone number) can also be requested from your teacher.",
        ],
      },
      {
        heading: "8. Students under 18",
        items: [
          "Many of our students are minors. A student under 18 should use the platform with the knowledge and consent of a parent or guardian, which is why we ask for a parent/guardian phone number. A parent or guardian can contact us to review or delete their child's information.",
        ],
      },
      {
        heading: "9. Changes to this policy",
        items: [
          "We may update this policy from time to time. The date at the top shows the latest version; continued use of the platform after a change means you accept it.",
        ],
      },
      {
        heading: "10. Contact us",
        items: ["For any privacy question or request, use the Contact page on this website."],
      },
    ],
  },
  terms: {
    title: "Terms of Use",
    intro:
      "These terms govern your use of the {name} learning platform (the website and the desktop app). By creating an account or using the platform you agree to them. If you do not agree, please do not use the platform.",
    sections: [
      {
        heading: "1. Your account",
        items: [
          "You must provide accurate information when you register and keep it up to date.",
          "Your account is personal: it may be used by you only. Sharing your account or password with anyone is not allowed.",
          "Each account can be used on a limited number of devices. Signing in from more devices than allowed may be blocked.",
          "You are responsible for everything done through your account, so keep your password secret.",
        ],
      },
      {
        heading: "2. Subscriptions and payments",
        items: [
          "Access to paid courses and lessons is granted for the subscription period or course you paid for, as set by your teacher.",
          "Fees, payment methods and refund conditions are those announced by your teacher at the time of subscription.",
          "Access may end when the subscription period ends or if these terms are violated.",
        ],
      },
      {
        heading: "3. Content ownership",
        items: [
          "All videos, files, exams and other materials on the platform are owned by {name} or their licensors and are protected by copyright law.",
          "You receive a personal, non-transferable, non-commercial right to view the content through the platform only while your subscription is active.",
        ],
      },
      {
        heading: "4. Prohibited actions",
        items: [
          "Recording the screen, taking screenshots, filming the screen, or downloading any content.",
          "Sharing, selling, publishing or re-uploading any content, in whole or in part, anywhere.",
          "Trying to bypass or disable the platform's protection (watermark, encryption, device limits, recording detection), or using virtual machines, scripts or tools for that purpose.",
          "Sharing your account, impersonating another person, or interfering with the platform or other users.",
        ],
      },
      {
        heading: "5. Enforcement",
        items: [
          "The platform automatically detects some violations, such as recording attempts. Violations may lead to a warning, a temporary or permanent suspension of the account, and cancellation of the subscription without refund.",
          "Leaked content can be traced back to the account that played it through the watermark. We reserve the right to take legal action against anyone who copies or distributes the content.",
        ],
      },
      {
        heading: "6. The desktop app",
        items: [
          "Download the desktop app only from this website or from the official Microsoft Store listing. Copies from other sources are not supported and may be unsafe.",
          "Some features require a stable internet connection and an up-to-date version of the app.",
        ],
      },
      {
        heading: "7. Availability and disclaimer",
        items: [
          "We work to keep the platform available and accurate, but it is provided \"as is\" and may occasionally be interrupted for maintenance, updates or reasons beyond our control.",
          "To the extent permitted by law, we are not liable for indirect losses arising from the use of, or inability to use, the platform.",
        ],
      },
      {
        heading: "8. Changes to these terms",
        items: [
          "We may update these terms from time to time. The date at the top shows the latest version; continued use of the platform after a change means you accept it.",
        ],
      },
      {
        heading: "9. Governing law",
        items: ["These terms are governed by the laws of the Arab Republic of Egypt."],
      },
      {
        heading: "10. Contact us",
        items: ["For any question about these terms, use the Contact page on this website."],
      },
    ],
  },
};

const ar = {
  privacy: {
    title: "سياسة الخصوصية",
    intro:
      "توضح هذه السياسة المعلومات التي تجمعها منصة {name} التعليمية (الموقع وتطبيق سطح المكتب)، وسبب جمعها، وكيف نحميها. بإنشائك حسابًا أو استخدامك للمنصة فإنك توافق على هذه السياسة.",
    sections: [
      {
        heading: "١. المعلومات التي نجمعها",
        items: [
          "بيانات الحساب: الاسم بالكامل، البريد الإلكتروني، رقم هاتف الطالب، رقم هاتف ولي الأمر، المحافظة والمدينة، الصف الدراسي، النوع، وصورة شخصية اختيارية. تُحفظ كلمة المرور بصورة مشفّرة فقط.",
          "بيانات التعلّم: الكورسات والاشتراكات، الدروس التي تفتحها وإلى أين وصلت في المشاهدة، إجابات الامتحانات ونتائجها، الرسائل التي ترسلها، والحصص المحجوزة.",
          "بيانات الجهاز والأمان: معرّف الجهاز واسمه (لتطبيق الحد المسموح من الأجهزة لكل حساب)، وأحداث الأمان مثل محاولات تصوير الشاشة أو تسجيلها أثناء فتح التطبيق (الاختصار المستخدم أو اسم برنامج التسجيل مع وقت المحاولة).",
          "بيانات تقنية: عنوان IP، إصدار المتصفح أو التطبيق، وسجلات الخادم اللازمة لتشغيل الخدمة وتأمينها.",
        ],
      },
      {
        heading: "٢. كيف نستخدم معلوماتك",
        items: [
          "لإنشاء حسابك، ومنحك الوصول إلى المحتوى الذي اشتركت فيه، وحفظ تقدّمك.",
          "لتطبيق مدد الاشتراك والحد المسموح من الأجهزة.",
          "لحماية المحتوى: تظهر على الفيديوهات علامة مائية متحركة تحمل اسمك ورقمك، وقد تؤدي محاولات التسجيل المكتشفة إلى تحذير أو إيقاف الحساب (راجع شروط الاستخدام).",
          "للتواصل معك أو مع ولي أمرك بخصوص الحساب والحصص والنتائج.",
          "للحفاظ على أمان المنصة وإصلاح المشكلات وتحسين الخدمة.",
        ],
      },
      {
        heading: "٣. مشاركة معلوماتك",
        items: [
          "لا نبيع معلوماتك الشخصية.",
          "يمكن لمعلّمك ({name}) ومساعديه المصرّح لهم الاطلاع على ملفك واشتراكاتك وتقدّمك ونتائج امتحاناتك ورسائلك.",
          "نستعين بمزوّدي خدمات موثوقين لاستضافة المنصة وتخزين الملفات (خوادم وتخزين سحابي)، ويعالجون البيانات فقط لتقديم هذه الخدمة.",
          "قد تُعرض بعض الدروس بفيديوهات مضمّنة من YouTube أو Vimeo، وتخضع تلك الخدمات لسياسات الخصوصية الخاصة بها.",
          "قد نفصح عن معلومات إذا تطلّب القانون ذلك أو لحماية حقوق المنصة ومستخدميها وسلامتهم.",
        ],
      },
      {
        heading: "٤. البيانات المحفوظة على جهازك",
        items: [
          "يحفظ الموقع وتطبيق سطح المكتب جلسة تسجيل الدخول واختيار اللغة وبعض البيانات المؤقتة (مثل جدولك) على جهازك حتى تبقى مسجّلًا ويعمل التطبيق أسرع. تسجيل الخروج يحذف جلسة الدخول.",
        ],
      },
      {
        heading: "٥. الأمان",
        items: [
          "جميع الاتصالات بالمنصة مشفّرة (HTTPS)، وكلمات المرور مشفّرة، والوصول إلى البيانات مقيّد حسب الصلاحيات. لا يوجد نظام آمن بنسبة ١٠٠٪، لكننا نتخذ إجراءات معقولة لحماية معلوماتك.",
        ],
      },
      {
        heading: "٦. مدة الاحتفاظ بالبيانات",
        items: [
          "نحتفظ بمعلوماتك طالما حسابك نشط. عند حذف الحساب نحذف معلوماتك الشخصية أو نجعلها مجهولة الهوية، باستثناء ما يلزم الاحتفاظ به لأسباب قانونية أو أمنية أو لحل النزاعات.",
        ],
      },
      {
        heading: "٧. حقوقك",
        items: [
          "يمكنك طلب الاطلاع على معلوماتك الشخصية أو تصحيحها أو حذفها في أي وقت من خلال صفحة التواصل. ويمكن أيضًا طلب بعض التعديلات (مثل الاسم أو رقم الهاتف) من معلّمك.",
        ],
      },
      {
        heading: "٨. الطلاب أقل من ١٨ سنة",
        items: [
          "كثير من طلابنا قُصّر. يجب أن يستخدم الطالب الأقل من ١٨ سنة المنصة بعلم وموافقة ولي أمره، ولهذا نطلب رقم هاتف ولي الأمر. ويمكن لولي الأمر التواصل معنا لمراجعة بيانات ابنه أو حذفها.",
        ],
      },
      {
        heading: "٩. التعديلات على هذه السياسة",
        items: [
          "قد نحدّث هذه السياسة من وقت لآخر. يوضح التاريخ في أعلى الصفحة آخر إصدار، واستمرارك في استخدام المنصة بعد التعديل يعني موافقتك عليه.",
        ],
      },
      {
        heading: "١٠. تواصل معنا",
        items: ["لأي سؤال أو طلب يخص الخصوصية، استخدم صفحة التواصل على هذا الموقع."],
      },
    ],
  },
  terms: {
    title: "شروط الاستخدام",
    intro:
      "تنظّم هذه الشروط استخدامك لمنصة {name} التعليمية (الموقع وتطبيق سطح المكتب). بإنشائك حسابًا أو استخدامك للمنصة فإنك توافق عليها. إذا لم توافق، يُرجى عدم استخدام المنصة.",
    sections: [
      {
        heading: "١. حسابك",
        items: [
          "يجب تقديم بيانات صحيحة عند التسجيل وتحديثها عند تغيّرها.",
          "الحساب شخصي ويستخدمه صاحبه فقط، ولا يُسمح بمشاركة الحساب أو كلمة المرور مع أي شخص.",
          "يمكن استخدام كل حساب على عدد محدود من الأجهزة، وقد يُمنع الدخول من أجهزة أكثر من المسموح.",
          "أنت مسؤول عن كل ما يتم من خلال حسابك، لذا حافظ على سرية كلمة المرور.",
        ],
      },
      {
        heading: "٢. الاشتراكات والمدفوعات",
        items: [
          "يُمنح الوصول إلى الكورسات والدروس المدفوعة طوال مدة الاشتراك أو للكورس الذي دفعت مقابله، حسب ما يحدده معلّمك.",
          "الرسوم وطرق الدفع وشروط الاسترداد هي التي يعلنها معلّمك وقت الاشتراك.",
          "قد ينتهي الوصول بانتهاء مدة الاشتراك أو عند مخالفة هذه الشروط.",
        ],
      },
      {
        heading: "٣. ملكية المحتوى",
        items: [
          "جميع الفيديوهات والملفات والامتحانات وغيرها من المواد على المنصة مملوكة لـ {name} أو للجهات المرخِّصة له، ومحمية بقوانين حقوق الملكية الفكرية.",
          "تحصل على حق شخصي وغير قابل للنقل وغير تجاري لمشاهدة المحتوى من خلال المنصة فقط طوال مدة اشتراكك.",
        ],
      },
      {
        heading: "٤. الأفعال المحظورة",
        items: [
          "تسجيل الشاشة أو تصويرها (Screenshot) أو تصويرها بالكاميرا، أو تحميل أي محتوى.",
          "مشاركة أي محتوى أو بيعه أو نشره أو إعادة رفعه، كليًا أو جزئيًا، في أي مكان.",
          "محاولة تجاوز حماية المنصة أو تعطيلها (العلامة المائية، التشفير، حد الأجهزة، رصد التسجيل)، أو استخدام أجهزة وهمية أو برامج أو أدوات لهذا الغرض.",
          "مشاركة الحساب، أو انتحال شخصية آخرين، أو التأثير على عمل المنصة أو على المستخدمين الآخرين.",
        ],
      },
      {
        heading: "٥. الإجراءات عند المخالفة",
        items: [
          "ترصد المنصة بعض المخالفات تلقائيًا مثل محاولات التسجيل. قد تؤدي المخالفة إلى تحذير، أو إيقاف الحساب مؤقتًا أو نهائيًا، وإلغاء الاشتراك دون استرداد المبلغ.",
          "يمكن تتبّع أي محتوى مسرّب إلى الحساب الذي شغّله من خلال العلامة المائية، ونحتفظ بحقنا في اتخاذ الإجراءات القانونية ضد كل من ينسخ المحتوى أو ينشره.",
        ],
      },
      {
        heading: "٦. تطبيق سطح المكتب",
        items: [
          "حمّل تطبيق سطح المكتب من هذا الموقع أو من صفحة التطبيق الرسمية على Microsoft Store فقط. النسخ من مصادر أخرى غير مدعومة وقد تكون غير آمنة.",
          "بعض المزايا تتطلب اتصالًا مستقرًا بالإنترنت وأحدث إصدار من التطبيق.",
        ],
      },
      {
        heading: "٧. توفّر الخدمة وإخلاء المسؤولية",
        items: [
          "نعمل على إبقاء المنصة متاحة ودقيقة، لكنها تُقدَّم \"كما هي\" وقد تتوقف أحيانًا للصيانة أو التحديث أو لأسباب خارجة عن إرادتنا.",
          "في حدود ما يسمح به القانون، لا نتحمّل مسؤولية الخسائر غير المباشرة الناتجة عن استخدام المنصة أو تعذّر استخدامها.",
        ],
      },
      {
        heading: "٨. التعديلات على هذه الشروط",
        items: [
          "قد نحدّث هذه الشروط من وقت لآخر. يوضح التاريخ في أعلى الصفحة آخر إصدار، واستمرارك في استخدام المنصة بعد التعديل يعني موافقتك عليه.",
        ],
      },
      {
        heading: "٩. القانون الحاكم",
        items: ["تخضع هذه الشروط لقوانين جمهورية مصر العربية."],
      },
      {
        heading: "١٠. تواصل معنا",
        items: ["لأي سؤال بخصوص هذه الشروط، استخدم صفحة التواصل على هذا الموقع."],
      },
    ],
  },
};

export const CONTENT = { en, ar };
